import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDialog } from "@/lib/dialog";
import { Button } from "@/components/ui/button";
import { BusyLabel } from "@/components/loading";
import { usePublicConfig } from "@/lib/publicConfig";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const errMsg = (e) => e?.response?.data?.detail || "No se pudo completar la acción";
const wa = (phone) => `https://wa.me/${String(phone || "").replace(/\D/g, "").replace(/^0/, "593")}`;
const NO_CUSTODY = ["PENDIENTE_PAGO", "PAGO_POR_VERIFICAR", "CANCELADO", "REEMBOLSADO", "LIBERADO", "REEMBOLSO_PENDIENTE", "RESUELTA_PARCIAL", "RECHAZADO"];

function PartyContact({ label, p, testid }) {
  if (!p) return null;
  return (
    <div className="bg-black/40 border border-[#262626] rounded p-3 text-xs space-y-1" data-testid={testid}>
      <div className="text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className="text-white font-semibold">{p.name}{p.city ? ` · ${p.city}` : ""}</div>
      {p.phone && <div className="flex gap-3"><a href={`tel:${p.phone}`} className="text-[#EF4444] inline-flex items-center gap-1"><Phone size={12} />{p.phone}</a>
        <a href={wa(p.phone)} target="_blank" rel="noreferrer" className="text-emerald-400 inline-flex items-center gap-1" data-testid={`${testid}-wa`}><MessageCircle size={12} />WhatsApp</a></div>}
      {p.email && <a href={`mailto:${p.email}`} className="text-[#EF4444] inline-flex items-center gap-1"><Mail size={12} />{p.email}</a>}
    </div>
  );
}

function AdminContact({ orderId }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/orders/${orderId}/contact`).then((r) => setC(r.data)).catch(() => setC(null)); }, [orderId]);
  if (!c) return null;
  return (
    <div className="grid sm:grid-cols-2 gap-2 mb-4" data-testid="admin-contact">
      <PartyContact label="Comprador" p={c.buyer} testid="admin-contact-buyer" />
      <PartyContact label="Vendedor" p={c.seller} testid="admin-contact-seller" />
    </div>
  );
}

// Controles de flujo del admin: cada acción pide motivo + confirmación y queda en auditoría.
export function AdminOrderControls({ order, reload }) {
  const { user } = useAuth();
  const { prompt, confirm } = useDialog();
  const [busy, setBusy] = useState(null);
  const enabled = usePublicConfig().admin_flow_controls === true;
  const st = order.status;
  const total = money(order.total);

  const run = async (key, { title, label, consequence, call, ok }) => {
    const reason = await prompt({ title, description: consequence, label: "Motivo (obligatorio, queda en el log de auditoría)", multiline: true, required: true });
    if (!reason) return;
    if (!(await confirm({ title: "¿Confirmas?", description: consequence, confirmText: "Sí, confirmar" }))) return;
    setBusy(key);
    try { await call(reason.trim()); toast.success(ok); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  const actions = [
    st === "PAGO_POR_VERIFICAR" && { key: "paid", text: "Marcar como pagado", busyText: "Confirmando…", cls: "bg-emerald-600 hover:bg-emerald-700",
      title: "Marcar como pagado", consequence: `Confirmas que HitTCG recibió ${total} por transferencia. El pedido pasa a PAGADO. No se recalcula ningún monto.`,
      call: (r) => api.post(`/admin/orders/${order.id}/transfer/confirm`, { amount_received: order.total, notes: r }), ok: "Pedido marcado como PAGADO" },
    st === "PAGADO" && { key: "ship", text: "Marcar como enviado", busyText: "Guardando…", cls: "bg-sky-600 hover:bg-sky-700",
      title: "Marcar como enviado", consequence: "El pedido pasa a ENVIADO y empieza el plazo normal de confirmación. No mueve dinero.",
      call: (r) => api.post(`/admin/orders/${order.id}/mark-shipped`, { reason: r }), ok: "Pedido marcado como ENVIADO" },
    st === "REEMBOLSO_PENDIENTE" && { key: "refund-close", text: "Marcar reembolso manual hecho y cerrar", busyText: "Cerrando…", cls: "bg-emerald-600 hover:bg-emerald-700",
      title: "Marcar reembolso manual hecho y cerrar", consequence: `Registra UN reembolso de ${total} al comprador como YA transferido por fuera. El pedido queda REEMBOLSADO y no se puede deshacer. La carta NO vuelve al catálogo.`,
      call: (r) => api.post(`/admin/orders/${order.id}/manual-refund-close`, { note: r }), ok: "Reembolso manual cerrado" },
    st !== "EN_DISPUTA" && { key: "dispute", text: "Abrir disputa", busyText: "Abriendo…", cls: "bg-amber-600 hover:bg-amber-700",
      title: "Abrir disputa (admin)", consequence: NO_CUSTODY.includes(st)
        ? `Este pedido no tiene dinero en custodia (${st}). Se registra un CASO sin cambiar el estado ni mover dinero.`
        : "El pedido pasa a EN_DISPUTA y se frena la liberación automática. No mueve dinero.",
      call: (r) => api.post(`/admin/orders/${order.id}/open-dispute`, { reason: r }), ok: "Disputa / caso registrado" },
    st === "EN_DISPUTA" && order.dispute?.opened_by_admin && !order.dispute?.resolution && { key: "nochange", text: "Cerrar disputa sin cambios", busyText: "Cerrando…", cls: "bg-neutral-700 hover:bg-neutral-600",
      title: "Cerrar disputa sin cambios", consequence: `El pedido vuelve a ${order.dispute.prev_status}. No mueve dinero.`,
      call: (r) => api.post(`/admin/orders/${order.id}/dispute/close-no-change`, { reason: r }), ok: "Disputa cerrada sin cambios" },
  ].filter(Boolean);

  const openCases = (order.admin_cases || []).filter((c) => c.status === "abierto");
  if (!enabled) return null;
  return (
    <div className="mb-5" data-testid="admin-order-controls">
      <AdminContact orderId={order.id} />
      {user?.role === "admin" && (
        <div className="flex flex-wrap gap-2">
          {actions.map((a) => (
            <Button key={a.key} data-testid={`admin-ctl-${a.key}`} disabled={!!busy} className={a.cls}
                    onClick={() => run(a.key, a)}>
              <BusyLabel busy={busy === a.key} text={a.busyText}>{a.text}</BusyLabel>
            </Button>
          ))}
        </div>
      )}
      {openCases.map((c) => (
        <div key={c.id} className="mt-3 p-3 border border-amber-500/40 bg-amber-500/5 rounded text-xs flex items-start justify-between gap-3" data-testid={`admin-case-${c.id}`}>
          <div><div className="text-amber-300 font-semibold flex items-center gap-1"><ShieldAlert size={12} />Caso abierto · {new Date(c.opened_at).toLocaleString()}</div>
            <div className="text-neutral-300 mt-1">{c.reason}</div></div>
          {user?.role === "admin" && (
            <Button size="sm" variant="outline" disabled={!!busy} data-testid={`admin-case-close-${c.id}`}
                    onClick={() => run(`case-${c.id}`, { title: "Cerrar caso", consequence: "Cierra el caso registrado. No cambia el estado ni mueve dinero.",
                      call: (r) => api.post(`/admin/orders/${order.id}/cases/${c.id}/close`, { reason: r }), ok: "Caso cerrado" })}>
              <BusyLabel busy={busy === `case-${c.id}`} text="Cerrando…">Cerrar caso</BusyLabel>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
